import classNames from "classnames";
import React, { useCallback, useMemo, useState, useEffect } from "react";
import { createStore, createStyleSet } from "botframework-webchat";

import WebChat from "./WebChat";
import BetaBanner from "./BetaBanner.js";
import * as logger from "./logger.js";
import updateIn from "simple-update-in";
import Timer from "./Timer.js";

import "./fabric-icons-inline.css";
import "./MinimizableWebChat.css";
import "animate.css";

import LccChatbotLogo from "./images/LCC Chatbot_blue_yellow outline.svg";
import LccChatbotLogoHover from "./images/LCC Chatbot_yellow_black.svg";
import LccChatbotIcon from "./images/LCC Chatbot_white.svg";

const TOKEN_SERVICE_URL = process.env.REACT_APP_TOKEN_SERVICE_URL;
const FEEDBACK_FORM_URL = process.env.REACT_APP_FEEDBACK_FORM_URL;
const CONVERSATION_TIMEOUT_SECONDS = parseInt(
  process.env.REACT_APP_CONVERSATION_TIMEOUT_SECONDS,
  10
);
const invalidTokenPlaceholder = "ErrorRetrievingToken";
const CONVERSATION_ID_STORAGE_NAME = "lccSavedConversationId";
const CONVERSATION_TIMESTAMP_STORAGE_NAME = "lccSavedConversationTimestamp";
const BOT_RESOURCE_PREFIX = "azbot-bots-contactcentre-";

const queryParams = new URLSearchParams(window.location.search);
const debug = queryParams.get("debug");

const MinimizableWebChat = () => {
  // restart session timer on each render as sessionTimeLimit is updated
  var sessionTimeLimit = new Date(
    new Date().getTime() + CONVERSATION_TIMEOUT_SECONDS * 1000
  );
  console.log(
    `MinimizableWebChat.resetSessionTimer ${sessionTimeLimit.getHours()}:${sessionTimeLimit.getMinutes()}:${sessionTimeLimit.getSeconds()}`
  );

  const styleSet = useMemo(
    () =>
      createStyleSet({
        backgroundColor: "Transparent",
      }),
    []
  );

  const [loaded, setLoaded] = useState(false);
  const [minimized, setMinimized] = useState(true);
  const side = "right";
  const [token, setToken] = useState();
  const [store, setStore] = useState();
  const [savedConversationId, setSavedConversationId] = useState(() => {
    const saved = sessionStorage.getItem(CONVERSATION_ID_STORAGE_NAME);
    console.log(`saved id=${saved}`);
    return saved || "";
  });
  const [savedConversationTimestamp, setSavedConversationTimestamp] = useState(
    () => {
      const saved = sessionStorage.getItem(CONVERSATION_TIMESTAMP_STORAGE_NAME);
      console.log(`saved time=${saved}`);
      return saved || "";
    }
  );

  useEffect(() => {
    sessionStorage.setItem(CONVERSATION_ID_STORAGE_NAME, savedConversationId);
    sessionStorage.setItem(
      CONVERSATION_TIMESTAMP_STORAGE_NAME,
      savedConversationTimestamp
    );
  }, [savedConversationId, savedConversationTimestamp]);

  const handleFetchToken = useCallback(async () => {
    if (!token) {
      console.log(`NODE_ENV=${process.env.NODE_ENV}`);
      console.log(`TOKEN_SERVICE_URL=${TOKEN_SERVICE_URL}`);
      console.log(
        `CONVERSATION_TIMEOUT_SECONDS=${CONVERSATION_TIMEOUT_SECONDS}`
      );

      var apiUrl = TOKEN_SERVICE_URL;
      var conversationTimestamp = savedConversationTimestamp
        ? savedConversationTimestamp
        : new Date(0);
      var savedConversationAge =
        (new Date().getTime() - new Date(conversationTimestamp).getTime()) /
        1000;
      console.log(`savedConversationAge=${savedConversationAge}`);

      if (
        savedConversationId &&
        savedConversationId != null &&
        savedConversationId !== "undefined"
      ) {
        if (
          !CONVERSATION_TIMEOUT_SECONDS ||
          !savedConversationAge ||
          savedConversationAge > CONVERSATION_TIMEOUT_SECONDS
        ) {
          console.log("conversation rejoin timeout exceeded");
        } else {
          console.log(
            `rejoining coversation with saved id=${savedConversationId}`
          );
          apiUrl = `${apiUrl}/${savedConversationId}`;
        }
      }
      console.log(`apiUrl=${apiUrl}`);

      let res = await fetch(apiUrl, {
        method: "GET",
      }).catch((err) => {
        logger.exception(err);
      });

      if (!res) {
        logger.error(
          `MinimizableWebChat.handleFetchToken() Error retrieving bot token from ${apiUrl}`
        );
        setToken(invalidTokenPlaceholder);
        return;
      }

      if (!res.ok) {
        let errorMessage = `MinimizableWebChat.handleFetchToken() Unsuccessful response from ${apiUrl} ${res.status} ${res.statusText}`;
        logger.error(errorMessage);
        setToken(invalidTokenPlaceholder);
        return;
      }

      const { token, conversationId } = await res.json();
      console.log(conversationId);

      if (!token) {
        let errorMessage = `MinimizableWebChat.handleFetchToken() Empty token from ${apiUrl} ${res.status} ${res.statusText}`;
        logger.error(errorMessage);
        setToken(invalidTokenPlaceholder);
        return;
      }

      logger.info("MinimizableWebChat.handleFetchToken() Bot token retrieved");
      setToken(token);
      setSavedConversationId(conversationId);
      setSavedConversationTimestamp(new Date());

      // Allow click tracking telemetry to also use conversationId customDimensions
      // so outgoing clicks in bot messages can be linked to conversations in App Insights logs
      logger.setConversationIdTelemetryField(conversationId);
    }
  }, [setToken, token, savedConversationId, savedConversationTimestamp]);

  const handleFetchStore = useCallback(() => {
    if (!store) {
      const store = createStore({}, ({ dispatch }) => (next) => (action) => {
        if (action.type === "DIRECT_LINE/CONNECT_FULFILLED") {
          dispatch({
            type: "WEB_CHAT/SEND_EVENT",
            payload: {
              name: "webchat/join",
              value: {
                language: window.navigator.language,
              },
            },
          });
        } else if (action.type === "DIRECT_LINE/INCOMING_ACTIVITY") {
          //workaround for old user messages appearing as bot messages https://github.com/microsoft/BotFramework-WebChat/issues/4331#issuecomment-1194715393
          if (
            !action.payload.activity.from.id.startsWith(BOT_RESOURCE_PREFIX)
          ) {
            action.payload.activity.from.role = "user";
          }
        }
        //add URL of page containing the webchat UI to the channel data for all outgoing requests
        if (action.type === "DIRECT_LINE/POST_ACTIVITY") {
          action = updateIn(
            action,
            ["payload", "activity", "channelData", "webchatPageUrl"],
            () => window.location.href
          );
          console.log("resetting conversation timestamp");
          setSavedConversationTimestamp(new Date());
        }

        return next(action);
      });
      setStore(store);
    }
  }, [setStore, store]);

  const handleMaximizeButtonClick = useCallback(async () => {
    setLoaded(true);
    setMinimized(false);
  }, [setMinimized]);

  const handleMinimizeButtonClick = useCallback(() => {
    logger.info(`MinimizableWebChat.handleMinimizeButtonClick`, {
      conversationId: savedConversationId,
    }); //for reporting

    //allow reconnection to conversation using saved conversation ID
    setMinimized(true);
  }, [setMinimized, savedConversationId]);

  const handleCloseButtonClick = useCallback(() => {
    logger.info("MinimizableWebChat.handleCloseButtonClick", {
      conversationId: savedConversationId,
    }); //for reporting

    //do not allow reconnection to conversation, delete store to clear conversation history
    setMinimized(true);
    setSavedConversationId("");
    setStore(null);
    setToken(null);
  }, [setMinimized, setSavedConversationId, savedConversationId]);

  const handleSessionTimerExpiry = () => {
    console.log("MinimizableWebChat.handleSessionTimerExpiry");

    setMinimized(true);
    setSavedConversationId("");
    setToken(null);
    setStore(null);
  };

  return (
    <div className="minimizable-web-chat">
      {minimized && (
        <button
          className={`maximize animate__animated animate__fadeInRight`}
          onClick={handleMaximizeButtonClick}
          title="Open the Leeds City Council chatbot"
          type="button"
        >
          <img src={LccChatbotLogo} alt="" />
          <img src={LccChatbotLogoHover} className="img-top" alt="" />
        </button>
      )}
      {loaded && (
        <div
          className={classNames(
            side === "left" ? "chat-box left" : "chat-box right",
            minimized ? "hide" : ""
          )}
        >
          <header>
            <div className="icon">
              <img src={LccChatbotIcon} alt="" />
            </div>
            <div className="filler" />
            <button
              className="minimize"
              aria-label="minimise"
              type="button"
              onClick={handleMinimizeButtonClick}
            >
              <span className="ms-Icon ms-Icon--ChromeMinimize" />
            </button>
            <button
              className="minimize"
              aria-label="close"
              type="button"
              onClick={handleCloseButtonClick}
            >
              <span className="ms-Icon ms-Icon--ChromeClose" />
            </button>
          </header>
          {/* until config file available with enable/disable flag, always display banner */}
          <BetaBanner
            displayBetaBanner={true}
            feedbackFormUrl={FEEDBACK_FORM_URL}
          />
          <div>
            <Timer
              expiryTimestamp={sessionTimeLimit}
              onExpire={handleSessionTimerExpiry}
              enableUi={debug}
            />
          </div>
          <WebChat
            className="react-web-chat"
            onFetchToken={handleFetchToken}
            onFetchStore={handleFetchStore}
            store={store}
            styleSet={styleSet}
            token={token}
            tokenInvalid={invalidTokenPlaceholder}
          />
        </div>
      )}
    </div>
  );
};

export default MinimizableWebChat;
